// Send to Planner: turns the VERA Operating Framework workbook (Action Log, Milestones) into Planner cards.
// Planner isn't a spreadsheet, so each row becomes a card: the columns Planner has (title, bucket, dates,
// progress, priority, owner) map across, and everything else goes into the card's notes so nothing is lost.

export type SheetKind = "actions" | "milestones";
export type Card = {
  key: string;            // the row's ID, e.g. A-006 or M-004
  source: string;         // Source / Forum (actions) or Workstream (milestones)
  milestone: string;      // Milestone ID (actions only)
  title: string;          // "A-006 · Team orientation pack…"
  bucket: string;
  start: string;          // yyyy-mm-dd or ""
  due: string;
  status: string;         // as in the workbook
  percent: 0 | 50 | 100;
  priority: 1 | 3 | 5 | 9;
  priorityLabel: string;
  owner: string;          // as written in the workbook ("Anand")
  flags: ("At Risk" | "Blocked")[];
  notes: string;
};
export type ParsedSheet = { kind: SheetKind; name: string; cards: Card[] };

const PRIORITY: Record<string, [Card["priority"], string]> = { critical: [1, "Urgent"], high: [3, "Important"], medium: [5, "Medium"], low: [9, "Low"] };
export const PROGRESS_LABEL: Record<Card["percent"], string> = { 0: "Not started", 50: "In progress", 100: "Completed" };

function text(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (v instanceof Date) return v.toISOString();
  if (typeof v === "object") {
    const o = v as { text?: unknown; result?: unknown; richText?: { text: string }[]; hyperlink?: string };
    if (o.richText) return o.richText.map((r) => r.text).join("");
    if (o.result !== undefined) return text(o.result);
    if (o.text !== undefined) return text(o.text);
    if (o.hyperlink) return o.hyperlink;
  }
  return String(v).trim();
}
function day(v: unknown): string {
  if (v instanceof Date) return `${v.getUTCFullYear()}-${String(v.getUTCMonth() + 1).padStart(2, "0")}-${String(v.getUTCDate()).padStart(2, "0")}`;
  if (typeof v === "object" && v && "result" in v) return day((v as { result: unknown }).result);
  const s = text(v);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
}
const niceDate = (d: string) => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

function progress(status: string): Card["percent"] {
  const s = status.toLowerCase();
  if (s === "complete" || s === "completed" || s === "done") return 100;
  if (s === "in progress" || s === "at risk" || s === "blocked") return 50;
  return 0;
}

/** Reads the workbook and returns every sheet Planner can take (Action Log and Milestones). */
export async function parseWorkbook(data: ArrayBuffer): Promise<ParsedSheet[]> {
  const { default: ExcelJS } = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(data);
  const out: ParsedSheet[] = [];
  wb.eachSheet((ws) => {
    // Find the header row: the first row (within the top 10) containing "Action ID" or "Milestone ID".
    let headerRow = 0; let kind: SheetKind | null = null;
    for (let r = 1; r <= Math.min(10, ws.rowCount); r++) {
      const vals = (ws.getRow(r).values as unknown[]).map(text);
      if (vals.includes("Action ID")) { headerRow = r; kind = "actions"; break; }
      if (vals.includes("Milestone ID")) { headerRow = r; kind = "milestones"; break; }
    }
    if (!kind) return;
    const head = (ws.getRow(headerRow).values as unknown[]).map(text);
    const col = (name: string) => head.indexOf(name);
    const cards: Card[] = [];
    for (let r = headerRow + 1; r <= ws.rowCount; r++) {
      const row = ws.getRow(r).values as unknown[];
      const get = (name: string) => (col(name) > 0 ? row[col(name)] : undefined);
      const id = text(get(kind === "actions" ? "Action ID" : "Milestone ID"));
      const what = text(get(kind === "actions" ? "Action / Deliverable" : "Milestone / Outcome"));
      if (!id || !what) continue;
      const status = text(get("Status")) || "Not Started";
      const pr = PRIORITY[text(get("Priority")).toLowerCase()] ?? PRIORITY.medium;
      const flags = (["At Risk", "Blocked"] as const).filter((f) => status.toLowerCase() === f.toLowerCase());
      const lines: string[] = [];
      const add = (label: string, v: string) => { if (v) lines.push(`${label}: ${v}`); };
      if (kind === "actions") {
        add("Action ID", id); add("Status in the framework", status); add("Support / Consult", text(get("Support / Consult")));
        add("Source / Forum", text(get("Source / Forum"))); add("Milestone", text(get("Milestone ID")));
        add("Dependency / Blocker", text(get("Dependency / Blocker"))); add("Latest update", text(get("Latest Update")));
        add("Completion evidence", text(get("Completion Evidence / Link"))); add("Closed", niceDate(day(get("Closed Date"))));
      } else {
        const pct = Number(text(get("% Complete")));
        add("Milestone ID", id); add("Status in the framework", status); add("Contributors", text(get("Contributors")));
        add("% complete", Number.isFinite(pct) && text(get("% Complete")) ? `${Math.round(pct <= 1 ? pct * 100 : pct)}%` : "");
        add("Acceptance criteria", text(get("Acceptance Criteria"))); add("Risks / issues", text(get("Risks / Issues")));
        add("Next update", text(get("Next Update"))); add("Source / basis", text(get("Source / Basis")));
      }
      lines.push("", `Sent from the VERA Operating Framework (${ws.name}) on ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}.`);
      cards.push({
        key: id,
        source: text(get(kind === "actions" ? "Source / Forum" : "Workstream")),
        milestone: kind === "actions" ? text(get("Milestone ID")) : "",
        title: `${id} · ${what}`.slice(0, 255),
        bucket: (text(get(kind === "actions" ? "Source / Forum" : "Workstream")) || "Imported").slice(0, 100),
        start: kind === "actions" ? day(get("Date Opened")) : "",
        due: day(get(kind === "actions" ? "Due Date" : "Target Date")),
        status, percent: progress(status), priority: pr[0], priorityLabel: pr[1],
        owner: text(get(kind === "actions" ? "Owner" : "Accountable Owner")),
        flags, notes: lines.join("\n"),
      });
    }
    if (cards.length) out.push({ kind, name: ws.name, cards });
  });
  return out.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === "actions" ? -1 : 1)); // Action Log first
}

export type Member = { id: string; name: string; first: string; mail: string };
/** Matches "Anand" or "Julija / Eva" to a member of the plan's group. Planner only assigns group members. */
export function matchOwner(owner: string, members: Member[]): Member | null {
  const first = owner.split(/[\/,(]/)[0].trim().toLowerCase();
  if (!first) return null;
  return members.find((m) => m.first.toLowerCase() === first || m.name.toLowerCase() === first || m.name.toLowerCase().startsWith(`${first} `) || m.mail.toLowerCase().startsWith(`${first}@`)) ?? null;
}
